"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { trackEvent } from "@/lib/analytics";
import {
  companionSectionLoadFailedEvent,
  companionSectionRetryEvent,
  eventOpenedEvent,
  forumRecommendationOpenedEvent,
} from "@/lib/companion/analytics-events";
import type {
  CompanionAroundYouRelevance,
  CompanionAroundYouResultKind,
  CompanionAroundYouSource,
  CompanionAroundYouStatus,
} from "@/lib/companion/around-you";
import type { PublicEventTrustState } from "@/lib/public-events/presentation";
import type { PublicEventCategory } from "@/lib/public-events/types";

type AroundYouLinkAnalytics =
  | {
      itemKind: "forum";
      relevanceKind: CompanionAroundYouRelevance;
      resultKind: CompanionAroundYouResultKind;
    }
  | {
      itemKind: "event";
      relevanceKind: CompanionAroundYouRelevance;
      verificationState: PublicEventTrustState;
      eventCategory: PublicEventCategory;
      resultKind: CompanionAroundYouResultKind;
    };

type CompanionAroundYouControlsProps =
  | {
      kind: "link";
      href: string;
      analytics: AroundYouLinkAnalytics;
      children: ReactNode;
      className: string;
    }
  | {
      kind: "retry";
      className: string;
    }
  | {
      kind: "failure";
      unavailableSourceCount: number;
    };

const AroundYouRetryContext = createContext<(() => void) | null>(null);

export function CompanionAroundYouInteractionBoundary({
  status,
  unavailableSources,
  children,
}: {
  status: CompanionAroundYouStatus;
  unavailableSources: CompanionAroundYouSource[];
  children: ReactNode;
}) {
  const router = useRouter();
  const [retryStarted, setRetryStarted] = useState(false);
  const [isPending, startTransition] = useTransition();

  const retry = () => {
    const event = companionSectionRetryEvent("around-you");
    try {
      trackEvent(event.name, event.properties);
    } catch {
      // Tracking must never prevent a normal route refresh.
    }

    setRetryStarted(true);
    startTransition(() => {
      router.refresh();
    });
  };

  const announcement =
    retryStarted && isPending
      ? "Refreshing Around you."
      : status === "partial"
        ? partialSourceAnnouncement(unavailableSources)
        : retryStarted
          ? retryOutcomeAnnouncement(status)
          : "";

  return (
    <AroundYouRetryContext.Provider value={retry}>
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="around-you-retry-status"
        className={
          status === "partial" && !isPending
            ? "mx-4 mt-4 rounded-sm border border-gold/35 bg-gold/10 px-3 py-2 text-[12px] leading-relaxed text-navy md:mx-5"
            : "sr-only"
        }
      >
        {announcement}
      </p>
      {children}
    </AroundYouRetryContext.Provider>
  );
}

export function CompanionAroundYouControls(
  props: CompanionAroundYouControlsProps,
) {
  if (props.kind === "failure") {
    return (
      <AroundYouFailureAnalytics
        unavailableSourceCount={props.unavailableSourceCount}
      />
    );
  }

  if (props.kind === "retry") {
    return <AroundYouRetryButton className={props.className} />;
  }

  const event =
    props.analytics.itemKind === "forum"
      ? forumRecommendationOpenedEvent({
          relevanceKind: props.analytics.relevanceKind,
          resultKind: props.analytics.resultKind,
        })
      : eventOpenedEvent({
          relevanceKind: props.analytics.relevanceKind,
          verificationState: props.analytics.verificationState,
          eventCategory: props.analytics.eventCategory,
          resultKind: props.analytics.resultKind,
        });

  return (
    <Link
      href={props.href}
      className={props.className}
      onClick={() => {
        try {
          trackEvent(event.name, event.properties);
        } catch {
          // Tracking must never interrupt normal Link navigation.
        }
      }}
    >
      {props.children}
    </Link>
  );
}

function AroundYouRetryButton({ className }: { className: string }) {
  const retry = useContext(AroundYouRetryContext);

  return (
    <button
      type="button"
      aria-label="Retry Around you"
      className={className}
      onClick={() => retry?.()}
    >
      Try again
    </button>
  );
}

function retryOutcomeAnnouncement(status: CompanionAroundYouStatus): string {
  switch (status) {
    case "ready":
      return "Around you refreshed.";
    case "partial":
      return "Around you refreshed. Some public noticeboard sources are temporarily unavailable.";
    case "empty":
      return "Around you refreshed. No moderated nearby event or contextual discussion was found.";
    case "no-base":
      return "Around you refreshed. Set your base to rank nearby updates.";
    case "error":
      return "Around you is still temporarily unavailable.";
  }
}

function partialSourceAnnouncement(
  unavailableSources: CompanionAroundYouSource[],
): string {
  if (
    unavailableSources.includes("forum") &&
    unavailableSources.includes("events")
  ) {
    return "Some public noticeboard sources are temporarily unavailable.";
  }
  if (unavailableSources.includes("forum")) {
    return "Public forum discussions are temporarily unavailable.";
  }
  if (unavailableSources.includes("events")) {
    return "Public events are temporarily unavailable.";
  }
  return "Some public noticeboard updates are temporarily unavailable.";
}

function AroundYouFailureAnalytics({
  unavailableSourceCount,
}: {
  unavailableSourceCount: number;
}) {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    const event = companionSectionLoadFailedEvent(
      "around-you",
      unavailableSourceCount,
    );
    try {
      trackEvent(event.name, event.properties);
    } catch {
      // Tracking must never interrupt section rendering.
    }
  }, [unavailableSourceCount]);

  return null;
}
